import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCancelSubscription } from '@pocapan/shared/domains/payments/ui/hooks/useCancelSubscription';
import { useCancelAutoTopUp } from '@pocapan/shared/domains/bettie/ui/hooks/useCancelAutoTopUp';
import type { RefundRequestReasonType } from './useBettieRefundRequest';

export const useSubscriptionCancel = () => {
  const navigate = useNavigate();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const searchParams = new URLSearchParams(window.location.search);
  const isAutoTopupCancel = searchParams.get('cancelTarget') === 'autoTopup';
  const cancelTarget = isAutoTopupCancel ? 'autoTopup' : 'subscription';
  const { mutateAsync: cancelSubscription } = useCancelSubscription();
  const { mutateAsync: cancelAutoTopUp } = useCancelAutoTopUp();

  const [selectedReasonType, setSelectedReasonType] = useState<RefundRequestReasonType>('pricey');
  const [cancelDetail, setCancelDetail] = useState('');

  const handleCancelSubscription = async () => {
    if (!subscriptionId) {
      return;
    }
    try {
      if (isAutoTopupCancel) {
        const body = {
          bettie_auto_topup: {
            cancel_type: selectedReasonType as 'pricey' | 'low_usage' | 'changed_my_mind' | 'others',
            cancel_detail: cancelDetail
          }
        };
        const response = await cancelAutoTopUp({ bettieAutoTopUpId: subscriptionId, body });
        if (response?.autoTopupId) {
          const successSearchParams = new URLSearchParams({
            cancel_date: response.canceledAt ?? '',
            cancelTarget
          });
          navigate(`/subscription/${subscriptionId}/cancel-success?${successSearchParams.toString()}`, {
            replace: true
          });
          return;
        }
        throw new Error('Failed to cancel auto top up');
      }

      const body = {
        subscription: {
          cancel_type: selectedReasonType as 'pricey' | 'low_usage' | 'changed_my_mind' | 'others',
          cancel_detail: cancelDetail
        }
      };
      const response = await cancelSubscription({ subscriptionId, body });
      if (response?.subscriptionId) {
        const successSearchParams = new URLSearchParams({
          cancel_date: response.canceledAt ?? '',
          cancelTarget
        });
        navigate(`/subscription/${subscriptionId}/cancel-success?${successSearchParams.toString()}`, {
          replace: true
        });
      } else {
        throw new Error('Failed to cancel subscription');
      }
    } catch (error) {
      console.error(error);
    }
  };
  return {
    handleCancelSubscription,
    isAutoTopupCancel,
    selectedReasonType,
    setSelectedReasonType,
    cancelDetail,
    setCancelDetail
  };
};
